///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import { shipperRatesViewColumns, shipperRatesViewColumnsV2 } from "../../../helpers/shipperRatesViewColumns";
import { analyticsView } from "../../../helpers/types";
import {
  filterLoads,
  setColumnWidth,
  sortLoads,
  toggleColumnFilterMenu
} from "../../../slices/analyticsSlice";

import LanesGridBody from "../pricing/LanesGridBody";
import PricingGridHeader from "../pricing/PricingGridHeader";
import Arrow from "../../icons/Arrow";
import { NoMatches } from "../lane-match/NoMatches";
import messages from "../../../helpers/messages";

export const ShipperRatesViewGrid = ({ division }) => {
  const {
    analytics: {
      selectedRowIndex,
      selectedButtonIndex,
      shipperRatesView: {
        lanes: shipperRates,
        laneOrigin,
        laneDestination,
        laneOriginRadius,
        laneDestinationRadius,
        columnFilterMenu,
        columnFilters,
        rangeFilters,
        columnWidths,
        sortField,
        sortOrder
      }
    },
    bidSummary: { summary }
  } = useSelector(state => state);
  const columns = summary?.isCoyoteBid ? shipperRatesViewColumns : shipperRatesViewColumnsV2;
  const columnOrder = columns.map(m => m.field);

  return (
    <div
      data-testid="shipperrates-view-grid"
      className="analytics-table-width shipper-rates"
      id="analytics-grid-shipperRatesView"
    >
      <div className="pt-8 pb-8 pl-24 bg-white">
        <span className="grid-heading">
          {messages.shipperRatesInformationFor.defaultMessage}
        </span>
        <div className="inline-grid ml-32 mr-32">
          <span className="origin-destination">{laneOrigin}</span>
          <span className="radius">
            <FormattedMessage
              {...messages.radiusMi}
              values={{ radius: laneOriginRadius }}
            />
          </span>
        </div>
        <Arrow
          width={16}
          height={16}
          style={{ top: 8, position: "relative", transform: `rotate(-90deg)` }}
        />
        <div className="inline-grid ml-32 mr-32">
          <span className="origin-destination">{laneDestination}</span>
          <span className="radius">
            <FormattedMessage
              {...messages.radiusMi}
              values={{ radius: laneDestinationRadius }}
            />
          </span>
        </div>
      </div>

      <div
        style={{ maxHeight: window.screen.height - 240 }}
        className="overflow-x--scroll scroll"
      >
        <table className="bg-white pl-16 pos-relative">
          <PricingGridHeader
            columns={columns}
            columnOrder={columnOrder}
            sortField={sortField}
            sortOrder={sortOrder}
            columnFilterMenu={columnFilterMenu}
            columnFilters={columnFilters}
            rangeFilters={rangeFilters}
            columnWidths={columnWidths}
            analyticsGridView={analyticsView.shipperRatesView}
            setColumnWidth={setColumnWidth}
            toggleColumnFilterMenu={toggleColumnFilterMenu}
            filterPricingLanes={filterLoads}
            sortPricingLanes={sortLoads}
            bidDivision={division ?? summary?.division}
          />
          {shipperRates?.length ? (
            <LanesGridBody
              columns={columns}
              lanes={shipperRates}
              offset={0}
              perPage={shipperRates.length}
              columnOrder={columnOrder}
              bidDivision={division ?? summary?.division}
              analyticsGridView={analyticsView.shipperRatesView}
              selectedRowIndex={selectedRowIndex}
              selectedButtonIndex={selectedButtonIndex}
              userDivision={division}
            />
          ) : null}
        </table>
      </div>

      {shipperRates?.length === 0 ? (
        <NoMatches
          heading={messages.noResults.defaultMessage}
          message={messages.noShipperRatesDataMessage.defaultMessage}
        />
      ) : null}
    </div>
  );
};

export default ShipperRatesViewGrid;
